// Tour validation utilities for concept vs published mode
// Concept tours only require a name; published tours require all critical fields
// Updated: Added image validation for publish, grouped error output for publish readiness

import { Tour } from '../types/tours';

export interface StepValidation {
  isComplete: boolean;
  errors: string[];
}

export interface PublishValidationGroup {
  step: number;
  label: string;
  errors: string[];
}

export type TourFormData = Partial<Tour> & { status?: 'concept' | 'published' };

export function validateBasics(data: TourFormData): StepValidation {
  const errors: string[] = [];
  if (!data.name?.trim()) errors.push('Tour name is required');
  return { isComplete: errors.length === 0, errors };
}

export function validateSchedule(data: TourFormData, isPublished: boolean): StepValidation {
  const errors: string[] = [];
  if (isPublished) {
    if (!data.location?.trim()) errors.push('Location is required');
    if (!data.departure?.trim()) errors.push('Departure time is required');
    if (!data.duration?.trim()) errors.push('Duration is required');
    if (!data.activity_level) errors.push('Activity level is required');
    if (!data.meeting_point?.trim()) errors.push('Meeting point is required');
    if (!data.meeting_point_url?.trim()) errors.push('Meeting point URL is required');
  }
  const hasAnyData = !!(
    data.location?.trim() ||
    data.departure?.trim() ||
    data.duration?.trim() ||
    data.activity_level ||
    data.meeting_point?.trim()
  );
  return { isComplete: isPublished ? errors.length === 0 : hasAnyData, errors };
}

export function validatePricing(data: TourFormData, isPublished: boolean): StepValidation {
  const errors: string[] = [];
  const hasGroupPrice = data.group_tour_price && data.group_tour_price > 0;
  const hasReducedPrice = data.reduced_group_price && data.reduced_group_price > 0;
  const hasPrivatePrice = data.private_price && data.private_price > 0;

  if (isPublished && !hasGroupPrice && !hasReducedPrice && !hasPrivatePrice) {
    errors.push('At least one price option is required');
  }
  const hasAnyData = !!(hasGroupPrice || hasReducedPrice || hasPrivatePrice);
  return { isComplete: isPublished ? errors.length === 0 : hasAnyData, errors };
}

export function validateDescriptions(data: TourFormData, isPublished: boolean): StepValidation {
  const errors: string[] = [];
  if (isPublished) {
    if (!data.tour_detail_headline?.trim()) errors.push('Headline is required');
    if (!data.tour_detail_description?.trim()) errors.push('Detailed description is required');
    if (!data.tour_card_description?.trim()) errors.push('Card description is required');
    if (!data.availability?.trim()) errors.push('Availability is required');
  }
  const hasAnyData = !!(
    data.tour_detail_headline?.trim() ||
    data.tour_detail_description?.trim() ||
    data.tour_card_description?.trim() ||
    data.availability?.trim()
  );
  return { isComplete: isPublished ? errors.length === 0 : hasAnyData, errors };
}

export function validateItems(data: TourFormData): StepValidation {
  const hasAnyData = !!((data.include_items?.length ?? 0) > 0 || (data.bring_items?.length ?? 0) > 0);
  return { isComplete: hasAnyData, errors: [] };
}

export function validateImages(data: TourFormData, isPublished: boolean): StepValidation {
  const errors: string[] = [];
  const carouselCount = data.carousel_images?.length ?? 0;

  if (isPublished && carouselCount === 0) {
    errors.push('At least one carousel image is required');
  }

  const hasAnyData = carouselCount > 0 || (data.gallery_images?.length ?? 0) > 0;
  return { isComplete: isPublished ? errors.length === 0 : hasAnyData, errors };
}

export function validateAllForPublish(data: TourFormData): string[] {
  const allErrors: string[] = [];
  allErrors.push(...validateBasics(data).errors);
  allErrors.push(...validateSchedule(data, true).errors);
  allErrors.push(...validatePricing(data, true).errors);
  allErrors.push(...validateDescriptions(data, true).errors);
  allErrors.push(...validateImages(data, true).errors);
  return allErrors;
}

export function validateAllForPublishGrouped(data: TourFormData): PublishValidationGroup[] {
  const groups: PublishValidationGroup[] = [];

  const basics = validateBasics(data);
  if (basics.errors.length > 0) {
    groups.push({ step: 0, label: 'Basics', errors: basics.errors });
  }

  const schedule = validateSchedule(data, true);
  if (schedule.errors.length > 0) {
    groups.push({ step: 1, label: 'Schedule & Logistics', errors: schedule.errors });
  }

  const pricing = validatePricing(data, true);
  if (pricing.errors.length > 0) {
    groups.push({ step: 2, label: 'Pricing', errors: pricing.errors });
  }

  const descriptions = validateDescriptions(data, true);
  if (descriptions.errors.length > 0) {
    groups.push({ step: 3, label: 'Descriptions', errors: descriptions.errors });
  }

  const images = validateImages(data, true);
  if (images.errors.length > 0) {
    groups.push({ step: 5, label: 'Images', errors: images.errors });
  }

  return groups;
}
